import React, { useEffect, useState } from "react";
import { Navigate, Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
  LayoutDashboard, Image as ImageIcon, FileText, Star, Layers, Lock, LogOut, Loader2, Save
} from "lucide-react";
import SectionsEditor from "./editors/SectionsEditor";
import MenuEditor from "./editors/MenuEditor";
import TestimonialsEditor from "./editors/TestimonialsEditor";
import PageBuilder from "./editors/PageBuilder";
import ChangePassword from "./editors/ChangePassword";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data));
  }, []);

  if (loading) return <div className="p-10 text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/admin" replace />;
  if (!content) return <div className="p-10 text-gray-500">Loading content…</div>;

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...content, ...patch };
      setContent(next);
      await api.put("/content", patch);
      setSavedAt(new Date());
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const reloadContent = async () => {
    const r = await api.get("/content");
    setContent(r.data);
  };

  const links = [
    { to: "/admin/dashboard", label: "Sections", icon: LayoutDashboard, end: true },
    { to: "/admin/dashboard/page-builder", label: "Page Builder", icon: Layers },
    { to: "/admin/dashboard/menu", label: "Menu Items", icon: FileText },
    { to: "/admin/dashboard/testimonials", label: "Testimonials", icon: Star },
    { to: "/admin/dashboard/password", label: "Change Password", icon: Lock },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside className="w-64 bg-[#1a1a1a] text-white flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="font-serif-display text-xl">The Benne Corner</div>
          <div className="text-xs text-white/50 mt-1">CMS Admin</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors ${
                  isActive ? "bg-[#a08862] text-white" : "text-white/70 hover:bg-white/5"
                }`
              }
            >
              <l.icon size={16} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-white/60 hover:text-white px-3 py-2"
          >
            <ImageIcon size={14} /> View Site
          </a>
          <button
            onClick={() => {
              logout();
              nav("/admin");
            }}
            className="flex items-center gap-2 text-xs text-white/60 hover:text-white px-3 py-2 w-full"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <header className="bg-white border-b sticky top-0 z-20 px-6 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-800">Dashboard</h2>
            <p className="text-xs text-gray-500">Logged in as {user.username}</p>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            {saving ? (
              <><Loader2 size={14} className="animate-spin" /> Saving…</>
            ) : savedAt ? (
              <><Save size={14} className="text-green-600" /> Saved {savedAt.toLocaleTimeString()}</>
            ) : null}
          </div>
        </header>
        <div className="p-6 max-w-5xl">
          <Routes>
            <Route index element={<SectionsEditor content={content} save={save} />} />
            <Route path="page-builder" element={<PageBuilder content={content} save={save} />} />
            <Route path="menu" element={<MenuEditor reload={reloadContent} />} />
            <Route path="testimonials" element={<TestimonialsEditor reload={reloadContent} />} />
            <Route path="password" element={<ChangePassword />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
